import type { ReactNode } from 'react'
import { useParams } from 'react-router-dom'

import { useEntitlements } from '@hapana/shared/web/hooks/entitlements.hooks'

import { useIsFeatureFlagEnabled, FeatureFlagKeys } from '_hooks/useFeatureFlag'
import { LayoutSkeleton } from '_components/page-skeletons'
import { EntitlementsContext } from '_contexts/entitlements.context'

interface SiteEntitlementsProviderProps {
  children: ReactNode
}

/**
 * Resolves the CURRENT LOCATION's entitlements for `/s/:siteId/*` routes and shadows the brand-level
 * `EntitlementsProvider` for everything under the site group. Site features (per-location retail,
 * integrations, ...) must gate on the site's plan — which folds in `Site.planTierOverride` — not the
 * brand's, so `FeatureGatedRoute` / `useCoreEntitlements` under `/s` read from here instead.
 *
 * `siteId` comes from the route param (the `s/:siteId` segment this provider mounts under). While the
 * site's entitlements load, render a Loading rather than the site content, so a gated site screen
 * never paints ungated then swaps to a lock/wall (the brand side does the same in the layout gate).
 * Fails open otherwise: on error or when tiering is off, every feature reads as entitled.
 */
export const SiteEntitlementsProvider = ({ children }: SiteEntitlementsProviderProps) => {
  const { siteId } = useParams()
  // Same product-tiering flag chokepoint as the brand provider (fail-closed, like the backend's
  // `isFeatureFlagEnabled`): only a confirmed-on flag fetches the site entitlements and enforces
  // gating; off/loading skips the fetch (fail-open ⇒ nothing gated).
  const tieringOn = useIsFeatureFlagEnabled(FeatureFlagKeys.ProductTiering)
  const entitlements = useEntitlements(siteId, tieringOn)

  if (siteId && entitlements.isLoading) {
    // Nav lives above this provider (in the layout); only the content area waits.
    return <LayoutSkeleton showNav={false} />
  }

  return (
    <EntitlementsContext.Provider value={entitlements}>{children}</EntitlementsContext.Provider>
  )
}
